import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const HeroContainer = styled.div`
  background: linear-gradient(135deg, #4caf50 0%, #2e7d32 100%);
  color: white;
  padding: 80px 0;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.5rem;
  margin-bottom: 30px;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const HeroButton = styled.a`
  display: inline-block;
  padding: 12px 30px;
  background-color: white;
  color: #2e7d32;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }
`;

const ServicesContainer = styled.div`
  padding: 80px 0;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 30px;
`;

const ServiceCard = styled.div`
  background-color: white;
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const ServiceIcon = styled.div`
  height: 200px;
  background-color: #e8f5e9;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: #2e7d32;
`;

const ServiceContent = styled.div`
  padding: 25px;
`;

const ServiceTitle = styled.h3`
  margin-bottom: 15px;
  color: #2e7d32;
  font-size: 1.5rem;
`;

const ServiceDescription = styled.p`
  color: #666;
  line-height: 1.6;
`;

const AboutContainer = styled.div`
  padding: 80px 0;
  background-color: #f5f5f5;
`;

const AboutContent = styled.div`
  display: flex;
  align-items: center;
  gap: 50px;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const AboutImage = styled.div`
  flex: 1;
  
  img {
    width: 100%;
    border-radius: 10px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  }
`;

const AboutText = styled.div`
  flex: 1;
`;

const AboutTitle = styled.h2`
  color: #2e7d32;
  margin-bottom: 20px;
  font-size: 2.5rem;
`;

const AboutDescription = styled.p`
  line-height: 1.8;
  margin-bottom: 20px;
  color: #444;
`;

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <>
      <HeroContainer>
        <div className="container">
          <HeroContent>
            <HeroTitle>{t('welcome')}</HeroTitle>
            <HeroSubtitle>{t('slogan')}</HeroSubtitle>
            <HeroButton href="#contact">{t('contact')}</HeroButton>
          </HeroContent>
        </div>
      </HeroContainer>
      
      <ServicesContainer>
        <div className="container">
          <h2 className="section-title">{t('services_title')}</h2>
          <ServicesGrid>
            <ServiceCard>
              <ServiceIcon>🏠</ServiceIcon>
              <ServiceContent>
                <ServiceTitle>{t('service1')}</ServiceTitle>
                <ServiceDescription>{t('service1_desc')}</ServiceDescription>
              </ServiceContent>
            </ServiceCard>
            
            <ServiceCard>
              <ServiceIcon>🏢</ServiceIcon>
              <ServiceContent>
                <ServiceTitle>{t('service2')}</ServiceTitle>
                <ServiceDescription>{t('service2_desc')}</ServiceDescription>
              </ServiceContent>
            </ServiceCard>
            
            <ServiceCard>
              <ServiceIcon>🧶</ServiceIcon>
              <ServiceContent>
                <ServiceTitle>{t('service3')}</ServiceTitle>
                <ServiceDescription>{t('service3_desc')}</ServiceDescription>
              </ServiceContent>
            </ServiceCard>
            
            <ServiceCard>
              <ServiceIcon>🪟</ServiceIcon>
              <ServiceContent>
                <ServiceTitle>{t('service4')}</ServiceTitle>
                <ServiceDescription>{t('service4_desc')}</ServiceDescription>
              </ServiceContent>
            </ServiceCard>
          </ServicesGrid>
        </div>
      </ServicesContainer>
      
      <AboutContainer>
        <div className="container">
          <AboutContent>
            <AboutImage>
              <img src="https://images.unsplash.com/photo-1581578731548-c64695cc6952/300x300" alt="Cleaning Service" />
            </AboutImage>
            <AboutText>
              <AboutTitle>{t('about_title')}</AboutTitle>
              <AboutDescription>{t('about_desc')}</AboutDescription>
              <HeroButton href="#contact" style={{ background: '#2e7d32', color: 'white' }}>{t('contact')}</HeroButton>
            </AboutText>
          </AboutContent>
        </div>
      </AboutContainer>
    </>
  );
};

export default HomePage;
